import type { JSX } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Plus, Search } from 'lucide-react'
import { BOT_STATES, wsRoom } from '@apiary/shared'
import type { BotState } from '@apiary/shared'
import type { BotDto } from '@/lib/dto'
import { isOnServer } from '@/lib/format'
import { useBots } from '@/hooks/queries'
import { useBotCommand, useDeleteBot } from '@/hooks/useBotCommands'
import { useWsRoom } from '@/hooks/useWsRoom'
import { useMinimaps } from '@/hooks/useMinimap'
import { useCanOperate, useSessionStore } from '@/stores/session'
import { EditBotDialog } from './EditBotDialog'
import { ConfirmDestructive } from '@/components/ConfirmDestructive'
import { Button } from '@/ui/primitives/Button'
import { Select } from '@/ui/primitives/Select'
import { SectionHead, Surface } from '@/ui/primitives/Surface'
import { BotTile } from './BotTile'

/**
 * Экран ботов. Плитки, поиск, фильтр по состоянию.
 *
 * Клавиатура: `/` — в поиск, `j`/`k` — двигать курсор, `Enter` — открыть.
 * Курсор появляется только после первого нажатия клавиши: за это отвечает
 * `useKeyboardMode`, поднятый в каркасе.
 */

const STATE_LABEL: Record<BotState, string> = {
  STOPPED: 'Остановлен',
  QUEUED: 'В очереди',
  CONNECTING: 'Подключение',
  LOGIN: 'Вход',
  CONFIG: 'Настройка',
  SPAWNING: 'Появление в мире',
  PLAYING: 'В игре',
  DEGRADED: 'Работает с оговорками',
  RECONNECT_WAIT: 'Ждёт переподключения',
  AUTH_FAILED: 'Ошибка авторизации',
  UNKNOWN: 'Состояние неизвестно'
}

export function BotsScreen({ initialState = '' }: { initialState?: string }): JSX.Element {
  const navigate = useNavigate()
  const orgId = useSessionStore(state => state.activeOrgId)
  useWsRoom(orgId === null ? null : wsRoom.org(orgId))

  const [query, setQuery] = useState('')
  const [state, setState] = useState<string>(initialState)
  const [cursor, setCursor] = useState(0)
  const [toEdit, setToEdit] = useState<BotDto | null>(null)
  const [toDelete, setToDelete] = useState<BotDto | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const bots = useBots({
    ...(query.trim() === '' ? {} : { q: query.trim() }),
    ...(state === '' ? {} : { state: state as BotState })
  })
  const canOperate = useCanOperate()
  const command = useBotCommand()
  const remove = useDeleteBot()

  const items = bots.data?.items ?? []
  // Мини-карты только у тех, кто в игре: у остальных срез считать нечего, а
  // подписка включила бы его в runner'е (док 08 §1).
  const minimaps = useMinimaps(items.filter(bot => isOnServer(bot.state)).map(bot => bot.id))

  const options = useMemo(
    () => [
      { value: '', label: 'Все состояния' },
      ...BOT_STATES.map(item => ({ value: item, label: STATE_LABEL[item] }))
    ],
    []
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      if (event.key === '/' && !typing) {
        event.preventDefault()
        searchRef.current?.focus()
        return
      }
      if (typing || items.length === 0) return
      if (event.key === 'j') setCursor(index => Math.min(items.length - 1, index + 1))
      if (event.key === 'k') setCursor(index => Math.max(0, index - 1))
      if (event.key === 'Enter') {
        const bot = items[cursor]
        if (bot !== undefined) void navigate({ to: '/bots/$botId', params: { botId: bot.id } })
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [items, cursor, navigate])

  return (
    <>
      <SectionHead
        actions={
          <>
            <div className="relative">
              <Search
                aria-hidden
                className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-faint"
              />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={event => setQuery(event.target.value)}
                aria-label="Поиск по имени бота"
                placeholder="Имя бота  /"
                className="h-9 w-44 rounded-sm border border-line bg-panel-2 py-1.5 pr-3 pl-8 text-base text-ink transition-colors duration-140 placeholder:text-ink-faint hover:border-line-strong focus:border-honey/60"
              />
            </div>
            <Select
              label="Состояние"
              hideLabel
              value={state}
              onValueChange={setState}
              options={options}
              className="w-52"
            />
            <Button
              variant="primary"
              disabled={!canOperate}
              onClick={() => void navigate({ to: '/bots/new' })}
            >
              <Plus aria-hidden className="size-4" />
              Создать бота
            </Button>
          </>
        }
      />

      {bots.isPending ? (
        <TileSkeletons />
      ) : items.length === 0 ? (
        <Surface variant="raised" className="px-6 py-12 text-center">
          <p className="font-display text-md font-semibold text-ink">
            {query === '' && state === '' ? 'Ботов пока нет' : 'Ничего не нашлось'}
          </p>
          <p className="mt-1 text-base text-ink-muted">
            {query === '' && state === ''
              ? 'Создайте первого: понадобится Minecraft-аккаунт и профиль сервера.'
              : 'Попробуйте изменить поиск или снять фильтр состояния.'}
          </p>
        </Surface>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {items.map((bot, index) => (
            <div key={bot.id} className="anim-rise" style={{ '--i': Math.min(index, 12) } as never}>
              <BotTile
                bot={bot}
                canOperate={canOperate}
                busy={command.isPending}
                cursor={index === cursor}
                minimap={minimaps[bot.id] ?? null}
                onStart={() =>
                  command.mutate({ botId: bot.id, botName: bot.name, command: 'start' })
                }
                onStop={() => command.mutate({ botId: bot.id, botName: bot.name, command: 'stop' })}
                onRestart={() =>
                  command.mutate({ botId: bot.id, botName: bot.name, command: 'restart' })
                }
                onEdit={() => setToEdit(bot)}
                onDelete={() => setToDelete(bot)}
                onCamera={() => void navigate({ to: '/bots/$botId', params: { botId: bot.id } })}
              />
            </div>
          ))}
        </div>
      )}

      <EditBotDialog bot={toEdit} onClose={() => setToEdit(null)} />

      <ConfirmDestructive
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete === null) return
          remove.mutate({ botId: toDelete.id, botName: toDelete.name })
          setToDelete(null)
        }}
        title={`Удалить бота «${toDelete?.name ?? ''}»?`}
        body="Бот и его история событий будут удалены. Minecraft-аккаунт и профиль сервера останутся."
        expected={toDelete?.name ?? ''}
        loading={remove.isPending}
      />
    </>
  )
}

/** Скелетоны той же формы, что плитки: иначе при загрузке страница «прыгает». */
function TileSkeletons(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {Array.from({ length: 6 }, (_unused, index) => (
        <Surface key={index} variant="raised" className="shimmer h-72" aria-hidden>
          <span className="sr-only">Загрузка</span>
        </Surface>
      ))}
    </div>
  )
}
